import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';
import { ArrowDown, ArrowUp, ArrowUpDown, Bell, BellOff, CarFront, Download, Home, MessageSquare, Plane, Send } from 'lucide-react';
import { endOfWeek, format, startOfWeek } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { DateRangePicker, Modal, Pagination } from '@/components/ui';
import { MultiSelect } from '@/components/ui/MultiSelect/MultiSelect';
import { useDebounce } from '@/hooks';
import {
  useEnableNotificationsMutation,
  useReservationsDashboardQuery,
  useSendCommentsMutation,
  useSendNotificationsMutation,
  exportTravelDashboard,
} from '@/services/reservations';
import { trackAction, trackPageView } from '@/utils/analytics';
import { cn } from '@/utils/cn';
import type {
  CommentsApiRequest,
  DashboardTab,
  ReservationGridPostFilter,
  ReservationWebResponse,
} from './types';

const tabs: DashboardTab[] = ['With Issues', 'Without Issues', 'All', 'Orphan'];
const statusOptions = ['Accepted', 'Cancelled', 'Denied', 'Invited', 'New', 'Pending'];
const issueOptions = [
  'Cancelled Flight',
  'Cancelled Ground Transportation',
  'Cancelled Hotel Booking',
  'Cancelled Hotel Booking Successfully',
  'Flight dates are not consistent with Registration dates',
  'Ground Transportation Date/Time is not consistent with Registration Date/Time',
  "Ground Transportation booking doesn't cover full guest stay",
  'Reservation dates are not consistent with Registration dates',
  'Pending Hotel Booking',
  'Pending Flight Booking',
  'Pending Ground Transportation Booking',
  'Pending Program Registration Confirmation',
  'Pending Hotel Booking Cancellation',
  'Pending Ground Transportation Booking Cancellation',
  'Pending Flight booking cancellation',
  'Not Required',
  'Not Booked',
  'Successfully Booked',
];

const commentSchema = z.object({
  commentText: z.string().trim().min(1, 'Comment text is required'),
});

type CommentFormValues = z.infer<typeof commentSchema>;
type TextFilterKey = 'guestName' | 'email' | 'type' | 'officeCity' | 'groupID' | 'hotelName' | 'reason';
type DateFilterKey = 'programsStartDate' | 'programsEndDate' | 'arrival' | 'departure';

const initialTextFilters: Record<TextFilterKey, string> = {
  guestName: '',
  email: '',
  type: '',
  officeCity: '',
  groupID: '',
  hotelName: '',
  reason: '',
};

const initialDateFilters: Record<DateFilterKey, string> = {
  programsStartDate: '',
  programsEndDate: '',
  arrival: '',
  departure: '',
};

function getCurrentWeekRange() {
  const today = new Date();
  return {
    startDate: startOfWeek(today, { weekStartsOn: 1 }),
    endDate: endOfWeek(today, { weekStartsOn: 1 }),
  };
}

function getStoredString(key: string) {
  return globalThis.sessionStorage?.getItem(key) ?? '';
}

function getStoredTab(): DashboardTab {
  const stored = getStoredString('dashboard-tabSelected');
  return tabs.includes(stored as DashboardTab) ? (stored as DashboardTab) : 'With Issues';
}

function normalizeCellValue(value: string | null | undefined) {
  return value?.trim() || '-';
}

function isClientReservation(reservation: ReservationWebResponse) {
  if (typeof reservation.isClient === 'boolean') return reservation.isClient;
  return (
    reservation.flightStatus?.trim().toUpperCase() === 'NOT REQUIRED' &&
    reservation.gtStatus?.trim().toUpperCase() === 'NOT REQUIRED'
  );
}

function isEligibleForNotification(reservation: ReservationWebResponse) {
  if (typeof reservation.isElgibleForNotification === 'boolean') {
    return reservation.isElgibleForNotification;
  }

  return (
    ['US', 'NON-US', 'NONUS'].includes(reservation.participantType?.trim().toUpperCase() ?? '') &&
    reservation.rentalCarStatusColorCode !== 'disabled'
  );
}

function getReservationKey(reservation: ReservationWebResponse) {
  return `${reservation.discrepancyId}-${reservation.registrationId ?? reservation.email ?? reservation.guestName}`;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = globalThis.URL.createObjectURL(blob);
  const link = globalThis.document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  globalThis.URL.revokeObjectURL(url);
}

function buildFilter(filterGridCol: string, filterValue: string): ReservationGridPostFilter | null {
  const trimmedValue = filterValue.trim();
  if (!trimmedValue || trimmedValue.length < 3) return null;

  return {
    filterValue: trimmedValue,
    filteDbCol: filterGridCol,
    filterGridCol,
    filterOperator: 'contains',
  };
}

function statusClass(status: string | null | undefined) {
  const normalized = status?.trim().toLowerCase();
  if (normalized === 'accepted') return 'text-primary-800';
  if (normalized === 'cancelled' || normalized === 'denied') return 'text-dds-red';
  return 'text-cool-gray-11';
}

function issueDotClass(colorCode: string | null | undefined) {
  if (colorCode === 'issue') return 'border-dds-red text-dds-red';
  if (colorCode === 'warning') return 'border-amber-500 text-amber-500';
  return 'border-cool-gray-2 text-cool-gray-2';
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(() => getCurrentWeekRange());
  const [tab, setTab] = useState<DashboardTab>(() => getStoredTab());
  const [searchText, setSearchText] = useState(() => getStoredString('dashboard-searchParam'));
  const debouncedSearchText = useDebounce(searchText, 500);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [textFilters, setTextFilters] = useState(() => initialTextFilters);
  const [dateFilters, setDateFilters] = useState(() => initialDateFilters);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
  const [commentTarget, setCommentTarget] = useState<ReservationWebResponse | null>(null);
  const [confirmNotifications, setConfirmNotifications] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [sortCol, setSortCol] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const { accounts } = useMsal();
  const alias = getStoredString('Alias');
  const loggedInUserEmail = accounts[0]?.username ?? '';
  const loggedInUserName = accounts[0]?.name ?? '';

  const commentForm = useForm<CommentFormValues>({
    resolver: zodResolver(commentSchema),
    defaultValues: { commentText: '' },
  });

  useEffect(() => {
    trackPageView('Dashboard', alias, 'Travel');
  }, [alias]);

  useEffect(() => {
    globalThis.sessionStorage?.setItem('dashboard-searchParam', debouncedSearchText);
  }, [debouncedSearchText]);

  const filters = useMemo(() => {
    const nextFilters = Object.entries(textFilters)
      .map(([key, value]) => buildFilter(key, value))
      .filter((filter): filter is ReservationGridPostFilter => filter !== null);

    Object.entries(dateFilters).forEach(([key, value]) => {
      if (value) {
        nextFilters.push({
          filterValue: value,
          filteDbCol: key,
          filterGridCol: key,
          filterOperator: 'contains',
        });
      }
    });

    if (selectedStatuses.length > 0) {
      nextFilters.push({
        filterValue: selectedStatuses.join(','),
        filteDbCol: 'status',
        filterGridCol: 'status',
        filterOperator: 'contains',
      });
    }

    if (selectedIssues.length > 0) {
      nextFilters.push({
        filterValue: selectedIssues.join(','),
        filteDbCol: 'issues',
        filterGridCol: 'issues',
        filterOperator: 'contains',
      });
    }

    return nextFilters;
  }, [dateFilters, selectedIssues, selectedStatuses, textFilters]);

  const dashboardRequest = useMemo(
    () => ({
      issueType: tab,
      startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
      endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
      searchText: debouncedSearchText.trim(),
      pageSize,
      page,
      reservationGridPostFilters: filters,
      sortCol: sortCol || undefined,
      sortDirection: sortDirection || undefined,
    }),
    [dateRange.endDate, dateRange.startDate, debouncedSearchText, filters, page, pageSize, sortCol, sortDirection, tab],
  );

  const dashboardQuery = useReservationsDashboardQuery(dashboardRequest);
  const enableNotificationsMutation = useEnableNotificationsMutation();
  const sendCommentsMutation = useSendCommentsMutation();
  const sendNotificationsMutation = useSendNotificationsMutation();

  const reservations = dashboardQuery.data?.reservations ?? [];
  const totalRecords = dashboardQuery.data?.totalRecords ?? 0;
  const allDiscrepancyIds = dashboardQuery.data?.allDiscripencyId ?? '';
  const selectedReservations = reservations.filter((reservation) => selectedIds.has(reservation.discrepancyId));
  const allRowsSelected = reservations.length > 0 && selectedReservations.length === reservations.length;
  const notificationIds = allRowsSelected
    ? allDiscrepancyIds
    : selectedReservations.map((reservation) => reservation.discrepancyId).join(',');
  const canExcludeNotifications = selectedReservations.some(
    (reservation) => reservation.enableNotifications && isEligibleForNotification(reservation),
  );
  const canIncludeNotifications =
    tab !== 'Orphan' &&
    selectedReservations.some(
      (reservation) => !reservation.enableNotifications && isEligibleForNotification(reservation),
    );
  const canSendNotifications =
    (tab === 'With Issues' || tab === 'Orphan') &&
    selectedReservations.some(
      (reservation) =>
        reservation.enableNotifications &&
        !reservation.isNotableGuest &&
        isEligibleForNotification(reservation),
    );
  const showSendNotification = tab === 'With Issues' || tab === 'Orphan';
  const attendeeCount = allRowsSelected
    ? allDiscrepancyIds.split(',').filter(Boolean).length
    : selectedReservations.filter(
        (reservation) => reservation.enableNotifications && !isClientReservation(reservation),
      ).length;

  const resetGridState = useCallback(() => {
    setPage(1);
    setPageSize(50);
    setSelectedIds(new Set());
    setTextFilters(initialTextFilters);
    setDateFilters(initialDateFilters);
    setSelectedStatuses([]);
    setSelectedIssues([]);
    setSortCol('');
    setSortDirection('');
  }, []);

  const handleDateRangeChange = useCallback(
    (range: { startDate: Date; endDate: Date }) => {
      setDateRange(range);
      trackAction('Dashboard', alias, 'calendar-apply', null, 'Travel');
      resetGridState();
    },
    [alias, resetGridState],
  );

  const handleDateRangeReset = useCallback(() => {
    setSearchText('');
    setDateRange(getCurrentWeekRange());
    trackAction('Dashboard', alias, 'calendar-reset', null, 'Travel');
    resetGridState();
  }, [alias, resetGridState]);

  function updateTextFilter(key: TextFilterKey, value: string) {
    setTextFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  }

  function updateDateFilter(key: DateFilterKey, value: string) {
    setDateFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  }

  const toggleSelected = useCallback((reservation: ReservationWebResponse, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(reservation.discrepancyId);
      else next.delete(reservation.discrepancyId);
      return next;
    });
  }, []);

  function toggleAllRows(checked: boolean) {
    setSelectedIds(checked ? new Set(reservations.map((reservation) => reservation.discrepancyId)) : new Set());
  }

  function handleTabChange(nextTab: DashboardTab) {
    setTab(nextTab);
    globalThis.sessionStorage?.setItem('dashboard-tabSelected', nextTab);
    trackAction('Dashboard', alias, `dashboard-tabSelected-${nextTab}`, null, 'Travel');
    resetGridState();
  }

  async function handleNotificationToggle(notificationStatus: boolean) {
    if (!notificationIds) return;
    setPageError(null);
    try {
      const success = await enableNotificationsMutation.mutateAsync({
        discrepancyIds: notificationIds,
        notificationStatus,
      });
      if (success) {
        toast.success(
          notificationStatus ? 'Notifications Included Successfully.' : 'Notifications Excluded Successfully.',
        );
        trackAction(
          'Dashboard',
          alias,
          notificationStatus ? 'include-notifications' : 'exclude-notifications',
          null,
          'Travel',
        );
        setSelectedIds(new Set());
      } else {
        toast.error('Something went wrong, Please contact DU ITS Team.');
      }
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Something went wrong. Please contact DU ITS Team.');
    }
  }

  async function handleSendComment(values: CommentFormValues) {
    if (!commentTarget) return;
    setPageError(null);
    const request: CommentsApiRequest = {
      eventId: commentTarget.groupID ?? '',
      registrationId: commentTarget.registrationId ?? '',
      fromEmail: loggedInUserEmail,
      toEmail: commentTarget.email ?? '',
      commentText: values.commentText,
      fromName: loggedInUserName,
      toName: commentTarget.guestName ?? '',
      isRemoved: false,
    };
    try {
      const success = await sendCommentsMutation.mutateAsync([request]);
      if (success) {
        toast.success('Comments Sent Successfully.');
        trackAction('Dashboard', alias, 'send-comments', null, 'Travel');
        setCommentTarget(null);
      } else {
        toast.error('Send Comment Failed, Please contact DU ITS Team.');
      }
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Send Comment Failed. Please contact DU ITS Team.');
    }
  }

  async function handleSendNotifications() {
    if (!notificationIds) return;
    setPageError(null);
    try {
      const success = await sendNotificationsMutation.mutateAsync({
        notifyUsers: notificationIds,
        loggedInUserEmail,
      });
      if (success) {
        toast.success('Notifications Sent Successfully.');
        trackAction('Dashboard', alias, 'send-notifications', null, 'Travel');
        setSelectedIds(new Set());
        setConfirmNotifications(false);
      } else {
        toast.error('Send Notification Failed, Please contact DU ITS Team.');
      }
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Send Notification Failed. Please contact DU ITS Team.');
    }
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      const workbook = await exportTravelDashboard(
        dateRange.startDate,
        dateRange.endDate,
        tab,
        debouncedSearchText,
      );
      downloadBlob(workbook, 'Travel Dashboard.xlsx');
      trackAction('Dashboard', alias, 'dashboard > export to excel', null, 'Travel');
    } catch (err) {
      // export errors are toast-only — don't pollute the grid's error banner
      toast.error(err instanceof Error ? err.message : 'Export failed. Please contact DU ITS Team.');
    } finally {
      setIsExporting(false);
    }
  }

  const openComments = useCallback((reservation: ReservationWebResponse) => {
    setCommentTarget(reservation);
    commentForm.reset({ commentText: '' });
  }, [commentForm]);

  const navigateToGuestDetails = useCallback((reservation: ReservationWebResponse) => {
    globalThis.sessionStorage?.setItem('reservation', JSON.stringify(reservation));
    navigate('/guest-details');
  }, [navigate]);

  function handleSort(col: string) {
    if (sortCol !== col) {
      setSortCol(col);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortCol('');
      setSortDirection('');
    }
    setPage(1);
  }

  return (
    <div className="space-y-5">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <h1 className="flex flex-wrap gap-2 text-2xl font-bold leading-9 text-black">
          <span>Reservations</span>
          <span className="font-light text-cool-gray-10">(Long-term)</span>
        </h1>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <label className="block w-full lg:w-80">
            <span className="sr-only">Search By Name, Email, Office or City, MMS ID</span>
            <input
              type="search"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(1);
                trackAction('Dashboard', alias, 'dashboard-searchParam', event.target.value, 'Travel');
              }}
              placeholder="Search By Name, Email, Office/City, MMS ID"
              disabled={dashboardQuery.isFetching}
              className="h-8 w-full border border-cool-gray-2 px-3 text-sm text-black placeholder:text-cool-gray-10 focus:border-black focus:outline-none focus:ring-0 disabled:bg-gray-50"
            />
          </label>
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onDateRangeChange={handleDateRangeChange}
            onReset={handleDateRangeReset}
            onlyInvokeResetHandler
          />
        </div>
      </section>

      <section className="space-y-3" aria-label="Reservations dashboard grid">
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-cool-gray-2 pt-4">
          <Toolbar
            canExcludeNotifications={canExcludeNotifications}
            canIncludeNotifications={canIncludeNotifications}
            canSendNotifications={canSendNotifications}
            showSendNotification={showSendNotification}
            hasRows={reservations.length > 0}
            isExporting={isExporting}
            isSending={sendNotificationsMutation.isPending}
            onExclude={() => void handleNotificationToggle(false)}
            onInclude={() => void handleNotificationToggle(true)}
            onSend={() => setConfirmNotifications(true)}
            onExport={() => void handleExport()}
          />
          <fieldset className="flex flex-wrap border-0 m-0 p-0">
            <legend className="sr-only">Reservation issue type</legend>
            {tabs.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => handleTabChange(item)}
                className={cn(
                  'border px-4 py-2 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1',
                  item === tab ? 'border-transparent bg-black text-white' : 'border-cool-gray-2 bg-white text-black hover:bg-gray-50',
                )}
                aria-pressed={item === tab}
              >
                {item}
              </button>
            ))}
          </fieldset>
        </div>

        {(dashboardQuery.isError || pageError !== null) && (
          <div role="alert" className="border border-dds-red bg-red-50 px-4 py-3 text-sm text-dds-red">
            {pageError ?? 'Unable to load reservations. Please try again.'}
          </div>
        )}

        <div className="relative overflow-x-auto overflow-y-auto max-h-[calc(100vh-320px)] min-h-[200px] border border-cool-gray-2">
          {dashboardQuery.isFetching && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
              aria-live="polite"
              aria-label="Loading reservations"
            >
              <span className="h-9 w-9 animate-spin rounded-full border-4 border-cool-gray-2 border-t-black" aria-hidden="true" />
            </div>
          )}
          <table
            aria-busy={dashboardQuery.isFetching}
            className="w-full table-fixed border-collapse bg-white"
          >
            <caption className="sr-only">Reservations</caption>
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="w-12 border-b border-cool-gray-2 bg-white px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allRowsSelected}
                    onChange={(event) => toggleAllRows(event.target.checked)}
                    aria-label="Select all reservations on this page"
                    className="h-4 w-4 border-cool-gray-2 text-dds-green focus:ring-dds-green"
                  />
                </th>
                <TableHeader title="Guest Name" className="w-48" sortKey="guestName" currentSortCol={sortCol} currentSortDir={sortDirection} onSort={handleSort}>
                  <ColumnTextFilter
                    label="Search Guest"
                    value={textFilters.guestName}
                    onChange={(value) => updateTextFilter('guestName', value)}
                  />
                </TableHeader>
                {tab === 'Orphan' ? (
                  <TableHeader title="Email" className="w-48">
                    <ColumnTextFilter
                      label="Search Email"
                      value={textFilters.email}
                      onChange={(value) => updateTextFilter('email', value)}
                    />
                  </TableHeader>
                ) : (
                  <TableHeader title="Type" className="w-40" sortKey="type" currentSortCol={sortCol} currentSortDir={sortDirection} onSort={handleSort}>
                    <ColumnTextFilter
                      label="Search Type"
                      value={textFilters.type}
                      onChange={(value) => updateTextFilter('type', value)}
                    />
                  </TableHeader>
                )}
                {tab !== 'Orphan' && (
                  <TableHeader title="Status" className="w-44" sortKey="status" currentSortCol={sortCol} currentSortDir={sortDirection} onSort={handleSort}>
                    <MultiSelect
                      placeholder="Select Status"
                      options={statusOptions.map((s) => ({ value: s, label: s }))}
                      value={selectedStatuses}
                      onChange={(value) => {
                        setSelectedStatuses(value);
                        setPage(1);
                      }}
                    />
                  </TableHeader>
                )}
                {tab !== 'Orphan' && (
                  <TableHeader title="Office/City" className="w-32" sortKey="officeCity" currentSortCol={sortCol} currentSortDir={sortDirection} onSort={handleSort}>
                    <ColumnTextFilter
                      label="Search Office"
                      value={textFilters.officeCity}
                      onChange={(value) => updateTextFilter('officeCity', value)}
                    />
                  </TableHeader>
                )}
                <TableHeader title="MMS ID" className="w-40" sortKey="groupID" currentSortCol={sortCol} currentSortDir={sortDirection} onSort={handleSort}>
                  <ColumnTextFilter
                    label="Search MMSID"
                    value={textFilters.groupID}
                    onChange={(value) => updateTextFilter('groupID', value)}
                  />
                </TableHeader>
                {tab !== 'Orphan' && (
                  <TableHeader title="Program Start Date" className="w-36" sortKey="programsStartDate" currentSortCol={sortCol} currentSortDir={sortDirection} onSort={handleSort}>
                    <ColumnDateFilter
                      label="Program start date"
                      value={dateFilters.programsStartDate}
                      onChange={(value) => updateDateFilter('programsStartDate', value)}
                    />
                  </TableHeader>
                )}
                {tab !== 'Orphan' && (
                  <TableHeader title="Program End Date" className="w-36" sortKey="programsEndDate" currentSortCol={sortCol} currentSortDir={sortDirection} onSort={handleSort}>
                    <ColumnDateFilter
                      label="Program end date"
                      value={dateFilters.programsEndDate}
                      onChange={(value) => updateDateFilter('programsEndDate', value)}
                    />
                  </TableHeader>
                )}
                <TableHeader title="Arrival" className="w-32" sortKey="arrival" currentSortCol={sortCol} currentSortDir={sortDirection} onSort={handleSort}>
                  <ColumnDateFilter
                    label="Arrival date"
                    value={dateFilters.arrival}
                    onChange={(value) => updateDateFilter('arrival', value)}
                  />
                </TableHeader>
                <TableHeader title="Departure" className="w-32" sortKey="departure" currentSortCol={sortCol} currentSortDir={sortDirection} onSort={handleSort}>
                  <ColumnDateFilter
                    label="Departure date"
                    value={dateFilters.departure}
                    onChange={(value) => updateDateFilter('departure', value)}
                  />
                </TableHeader>
                {tab === 'Orphan' ? (
                  <TableHeader title="Reasons" className="w-48">
                    <ColumnTextFilter
                      label="Reason"
                      value={textFilters.reason}
                      onChange={(value) => updateTextFilter('reason', value)}
                    />
                  </TableHeader>
                ) : (
                  <TableHeader title="Hotel Name" className="w-40" sortKey="hotelName" currentSortCol={sortCol} currentSortDir={sortDirection} onSort={handleSort}>
                    <ColumnTextFilter
                      label="Search Hotel"
                      value={textFilters.hotelName}
                      onChange={(value) => updateTextFilter('hotelName', value)}
                    />
                  </TableHeader>
                )}
                {tab !== 'Orphan' && (
                  <TableHeader title="Issues" className="w-52">
                    <MultiSelect
                      placeholder="Select Issue"
                      options={issueOptions.map((s) => ({ value: s, label: s }))}
                      value={selectedIssues}
                      onChange={(value) => {
                        setSelectedIssues(value);
                        setPage(1);
                      }}
                    />
                  </TableHeader>
                )}
                {tab !== 'Orphan' && <TableHeader title="Actions" className="w-24" />}
              </tr>
            </thead>
            <tbody>
              {dashboardQuery.isFetching && reservations.length === 0 && (
                <tr>
                  <td colSpan={14} className="px-4 py-8 text-center text-sm text-cool-gray-10">
                    Loading reservations...
                  </td>
                </tr>
              )}
              {!dashboardQuery.isFetching && reservations.length === 0 && (
                <tr>
                  <td colSpan={14} className="px-4 py-8 text-center text-sm text-cool-gray-10">
                    No reservations found.
                  </td>
                </tr>
              )}
              {reservations.map((reservation) => (
                <ReservationRow
                  key={getReservationKey(reservation)}
                  reservation={reservation}
                  tab={tab}
                  checked={selectedIds.has(reservation.discrepancyId)}
                  onToggle={toggleSelected}
                  onOpenComments={openComments}
                  onNavigate={navigateToGuestDetails}
                />
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </section>

      <Modal
        isOpen={commentTarget !== null}
        onClose={() => setCommentTarget(null)}
        title={commentTarget ? `Send Comments (${commentTarget.guestName} - ${commentTarget.groupID})` : 'Send Comments'}
        className="max-w-2xl rounded-none"
      >
        {commentTarget && (
          <form onSubmit={(event) => void commentForm.handleSubmit(handleSendComment)(event)} className="mt-5 space-y-4">
            <div>
              <label htmlFor="commentText" className="mb-1 block text-sm font-semibold text-black">
                Comment Text
              </label>
              <textarea
                id="commentText"
                rows={4}
                placeholder="Write your comment here..."
                className="w-full border border-cool-gray-2 px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-0"
                aria-invalid={!!commentForm.formState.errors.commentText}
                aria-describedby={commentForm.formState.errors.commentText ? 'commentText-error' : undefined}
                {...commentForm.register('commentText')}
              />
              {commentForm.formState.errors.commentText && (
                <p id="commentText-error" className="mt-1 text-sm text-dds-red">
                  {commentForm.formState.errors.commentText.message}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCommentTarget(null)}
                className="border border-cool-gray-2 bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sendCommentsMutation.isPending}
                className="border border-dds-green bg-dds-green px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sendCommentsMutation.isPending ? 'Send a comment...' : 'Send a comment'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={confirmNotifications}
        onClose={() => setConfirmNotifications(false)}
        title="Confirm Notifications"
        className="max-w-lg rounded-none text-center"
      >
          <p className="mt-4 text-sm text-cool-gray-11">
            Notification(s) are being sent to <strong>{attendeeCount}</strong> attendees. Are you sure you want to continue?
          </p>
          <div className="mt-6 flex justify-center gap-2">
            <button
              type="button"
              onClick={() => void handleSendNotifications()}
              className="border border-dds-green bg-dds-green px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmNotifications(false);
                setSelectedIds(new Set());
              }}
              className="border border-cool-gray-2 bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
            >
              No
            </button>
          </div>
      </Modal>
    </div>
  );
}

const Toolbar = memo(function Toolbar({
  canExcludeNotifications,
  canIncludeNotifications,
  canSendNotifications,
  showSendNotification,
  hasRows,
  isExporting,
  isSending,
  onExclude,
  onInclude,
  onSend,
  onExport,
}: Readonly<{
  canExcludeNotifications: boolean;
  canIncludeNotifications: boolean;
  canSendNotifications: boolean;
  showSendNotification: boolean;
  hasRows: boolean;
  isExporting: boolean;
  isSending: boolean;
  onExclude: () => void;
  onInclude: () => void;
  onSend: () => void;
  onExport: () => void;
}>) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ToolbarButton disabled={!canExcludeNotifications} onClick={onExclude} icon={<BellOff size={16} aria-hidden="true" />}>
        Exclude notifications
      </ToolbarButton>
      <ToolbarButton disabled={!canIncludeNotifications} onClick={onInclude} icon={<Bell size={16} aria-hidden="true" />}>
        Include notifications
      </ToolbarButton>
      {showSendNotification && (
        <ToolbarButton disabled={!canSendNotifications || isSending} onClick={onSend} icon={<Send size={16} aria-hidden="true" />}>
          {isSending ? 'Sending Notifications...' : 'Send notification'}
        </ToolbarButton>
      )}
      <ToolbarButton disabled={!hasRows || isExporting} onClick={onExport} icon={<Download size={16} aria-hidden="true" />}>
        Export
      </ToolbarButton>
    </div>
  );
});

function ToolbarButton({
  children,
  disabled,
  icon,
  onClick,
  className,
}: Readonly<{
  children: React.ReactNode;
  disabled: boolean;
  icon: React.ReactNode;
  onClick: () => void;
  className?: string;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-2 border border-dds-green bg-dds-green px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1 disabled:cursor-not-allowed disabled:border-cool-gray-2 disabled:bg-cool-gray-2 disabled:text-cool-gray-10',
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function SortIcon({ col, currentSortCol, currentSortDir }: Readonly<{ col: string; currentSortCol?: string; currentSortDir?: string }>) {
  if (currentSortCol !== col) return <ArrowUpDown size={12} className="opacity-30" aria-hidden="true" />;
  if (currentSortDir === 'asc') return <ArrowUp size={12} aria-hidden="true" />;
  return <ArrowDown size={12} aria-hidden="true" />;
}

function TableHeader({
  children,
  title,
  className,
  sortKey,
  currentSortCol,
  currentSortDir,
  onSort,
}: Readonly<{
  children?: React.ReactNode;
  title: string;
  className?: string;
  sortKey?: string;
  currentSortCol?: string;
  currentSortDir?: string;
  onSort?: (col: string) => void;
}>) {
  let sortValue: 'ascending' | 'descending' | 'none' | undefined;
  if (sortKey) {
    if (currentSortCol === sortKey) {
      sortValue = currentSortDir === 'asc' ? 'ascending' : 'descending';
    } else {
      sortValue = 'none';
    }
  }
  return (
    <th
      aria-sort={sortValue}
      className={cn('border-b border-cool-gray-2 bg-white px-3 py-3 text-left align-top text-sm font-bold text-black', className)}
    >
      <div className="space-y-2">
        {sortKey && onSort ? (
          <button
            type="button"
            onClick={() => onSort(sortKey)}
            className="flex items-center gap-1 whitespace-nowrap font-bold text-black hover:text-black focus:outline-none"
          >
            {title}
            <SortIcon col={sortKey} currentSortCol={currentSortCol} currentSortDir={currentSortDir} />
          </button>
        ) : (
          <span className="whitespace-nowrap">{title}</span>
        )}
        {children}
      </div>
    </th>
  );
}

function ColumnTextFilter({
  label,
  value,
  onChange,
}: Readonly<{ label: string; value: string; onChange: (value: string) => void }>) {
  return (
    <input
      type="text"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={label}
      aria-label={label}
      className="h-8 w-full border border-cool-gray-2 px-2 text-xs font-normal text-black placeholder:text-cool-gray-10 focus:border-black focus:outline-none focus:ring-0"
    />
  );
}

function ColumnDateFilter({
  label,
  value,
  onChange,
}: Readonly<{ label: string; value: string; onChange: (value: string) => void }>) {
  return (
    <input
      type="date"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label={label}
      className="h-8 w-full border border-cool-gray-2 px-2 text-xs font-normal text-black focus:border-black focus:outline-none focus:ring-0"
    />
  );
}

const ReservationRow = memo(function ReservationRow({
  reservation,
  tab,
  checked,
  onToggle,
  onOpenComments,
  onNavigate,
}: Readonly<{
  reservation: ReservationWebResponse;
  tab: DashboardTab;
  checked: boolean;
  onToggle: (reservation: ReservationWebResponse, checked: boolean) => void;
  onOpenComments: (reservation: ReservationWebResponse) => void;
  onNavigate: (reservation: ReservationWebResponse) => void;
}>) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="border-b border-cool-gray-2 px-3 py-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onToggle(reservation, event.target.checked)}
          aria-label={`Select ${reservation.guestName ?? 'reservation'}`}
          className="h-4 w-4 border-cool-gray-2 text-dds-green focus:ring-dds-green"
        />
      </td>
      <td className="border-b border-cool-gray-2 px-3 py-3 text-sm">
        <button
          type="button"
          onClick={() => onNavigate(reservation)}
          className="w-full text-left font-semibold uppercase text-dds-blue hover:underline focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
        >
          {normalizeCellValue(reservation.guestName)}
        </button>
      </td>
      {tab === 'Orphan' ? <Cell>{reservation.email}</Cell> : <Cell>{reservation.type}</Cell>}
      {tab !== 'Orphan' && (
        <td className="border-b border-cool-gray-2 px-3 py-3 text-sm whitespace-nowrap">
          <span className={cn('text-xs font-semibold', statusClass(reservation.status))}>
            {normalizeCellValue(reservation.status)}
          </span>
        </td>
      )}
      {tab !== 'Orphan' && <Cell>{reservation.officeCity}</Cell>}
      <Cell>{reservation.groupID}</Cell>
      {tab !== 'Orphan' && <Cell>{reservation.programsStartDate}</Cell>}
      {tab !== 'Orphan' && <Cell>{reservation.programsEndDate}</Cell>}
      <Cell>{reservation.arrival}</Cell>
      <Cell>{reservation.departure}</Cell>
      {tab === 'Orphan' ? <Cell>{reservation.reason}</Cell> : <Cell>{reservation.hotelName}</Cell>}
      {tab !== 'Orphan' && (
        <td className="border-b border-cool-gray-2 px-3 py-3 text-sm whitespace-nowrap">
          <div className="flex gap-1.5" aria-label="Reservation issue statuses">
            <IssueDot label={reservation.customFlightStatus ?? 'Flight'} colorCode={reservation.flightStatusColorCode} icon={<Plane size={12} aria-hidden="true" />} />
            <IssueDot label={reservation.customGTStatus ?? 'Ground transportation'} colorCode={reservation.rentalCarStatusColorCode} icon={<CarFront size={12} aria-hidden="true" />} />
            <IssueDot label={reservation.customHotelStatus ?? 'Hotel'} colorCode={reservation.hotelStatusColorCode} icon={<Home size={12} aria-hidden="true" />} />
          </div>
        </td>
      )}
      {tab !== 'Orphan' && (
        <td className="border-b border-cool-gray-2 px-3 py-3 text-sm whitespace-nowrap">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenComments(reservation)}
              className="inline-flex items-center gap-1 text-dds-blue hover:underline focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
              aria-label={`Send comment to ${reservation.guestName ?? 'guest'}`}
            >
              <MessageSquare size={16} aria-hidden="true" />
              {reservation.commentsCount > 0 && (
                <span aria-label={`${reservation.commentsCount} comment${reservation.commentsCount !== 1 ? 's' : ''}`}>
                  {reservation.commentsCount}
                </span>
              )}
            </button>
            {reservation.enableNotifications &&
            !isClientReservation(reservation) &&
            !reservation.isNotableGuest &&
            reservation.issueType === 'With Issues' ? (
              <Bell size={16} className="text-cool-gray-10" aria-label="Notifications enabled" />
            ) : (
              <BellOff size={16} className="text-cool-gray-10" aria-label="Notifications disabled" />
            )}
            {reservation.notificationCount > 0 && !isClientReservation(reservation) && (
              <span aria-label={`${reservation.notificationCount} notification${reservation.notificationCount !== 1 ? 's' : ''}`}>
                {reservation.notificationCount}
              </span>
            )}
          </div>
        </td>
      )}
    </tr>
  );
});

function Cell({ children }: Readonly<{ children: string | null | undefined }>) {
  return <td className="border-b border-cool-gray-2 px-3 py-3 text-sm">{normalizeCellValue(children)}</td>;
}

function IssueDot({ label, colorCode, icon }: Readonly<{ label: string; colorCode: string | null | undefined; icon: React.ReactNode }>) {
  return (
    <span
      title={label}
      aria-label={label}
      className={cn('inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border', issueDotClass(colorCode))}
    >
      <span aria-hidden="true">{icon}</span>
    </span>
  );
}